import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const NotificationContext = createContext(null);

let toastIdCounter = 0;

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    message: '',
    resolve: null
  });

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = null) => {
    const id = ++toastIdCounter;
    const toast = {
      id,
      message,
      type, // 'success', 'error', 'info', 'warning'
      duration: duration || (type === 'error' || type === 'warning' ? 6000 : 4000)
    };

    setToasts(prev => {
      // The same message twice (a double save, a repeated poll) shows once, as
      // the newest; at most 3 on screen so the stack never buries the map.
      const newToasts = [...prev.filter(t => t.message !== message || t.type !== type), toast];
      return newToasts.slice(-3);
    });

    // Auto-dismiss
    setTimeout(() => {
      dismissToast(id);
    }, toast.duration);

    return id;
  }, [dismissToast]);

  const showConfirm = useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        show: true,
        message,
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback((confirmed) => {
    setConfirmDialog(prev => {
      if (prev.resolve) {
        prev.resolve(confirmed);
      }
      return {
        show: false,
        message: '',
        resolve: null
      };
    });
  }, []);

  const value = useMemo(() => ({
    showToast,
    dismissToast,
    showConfirm,
    toasts,
    confirmDialog,
    handleConfirm
  }), [showToast, dismissToast, showConfirm, toasts, confirmDialog, handleConfirm]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};
